import api from "@/config/api";
import useUser from "@/context/user";
import { User } from "@/types/user";
import { User as FirebaseUser } from "firebase/auth";
import { CustomClaims } from "@/types/utils";
import router from "@/router";
import storage from "./storage";
import { FileDescriptor } from "@/components/common/modals/file-viewer/context";
import constant from "./constant";
import { i18n } from "./i18n";

const setCurrentUser = async (auth: FirebaseUser) => {
    const login = useUser.getState().login;
    const pages = getAuthenticatedPages();
    const isOnAuthenticatedPage = Object.values(pages).some(i => i === true);

    const { organizations } = await api.get<User>("/users");
    const oids = Object.keys(organizations);
    const oid = storage.getItem("oid");
    const claims = await getClaims(auth);

    // If there is oid in Local Storage and in Firebase claims but they don't match OR oid exists in Firebase claims but not in Local Storage, then save Firebase claims oid to Local Storage and use it to fetch entities.
    if (
        (oid && claims.organization?.oid && oid !== claims.organization?.oid) ||
        (claims.organization?.oid && !oid)
    ) {
        storage.setItem("oid", claims.organization?.oid);
        login();

        return router.navigate(`/organizations/${claims.organization?.oid}/reports`);
    }

    if (oid && oids.includes(oid)) {
        // If there is oid in Local Storage but not in Firebase claims, then set Local Storage oid to Firebase claims.
        if (!claims.organization?.oid) await api.post("/users/set-claims", { data: { oid } });

        login();

        // Check if oid in current URL is different from the one that is in Local Storage. If they are different, then redirect user to a new URL with correct oid in it.
        const oidInCurrentURL = router.state.location.pathname
            .match(/organization\/([a-z0-9]+)/gi)?.[0]
            .split("/")[1];

        if (oidInCurrentURL && oidInCurrentURL !== oid) {
            return router.navigate(`/organizations/${oid}/reports`);
        }

        if (isOnAuthenticatedPage) return;
        return router.navigate(`/organizations/${oid}/reports`);
    }

    // If user doesn't have oid in Local Storage, neither in Firebase claims but user is part of more than one organizations then redirect to Select Organization page.
    if (!oid && !claims.organization?.oid && oids.length > 1) {
        login();
        return router.navigate("/organizations");
    }

    // If user doesn't have oid in Local Storage, neither in Firebase claims and user is part of just one organization, then automatically select that organization.
    if (!oid && !claims.organization?.oid && oids.length === 1 && oids[0]) {
        const oid = oids[0];
        await api.post("/users/set-claims", { data: { oid } });
        storage.setItem("oid", oid);

        login();
        return router.navigate(`/organizations/${oid}/reports`);
    }
};

const getClaims = async (user: FirebaseUser) => {
    const result = await user.getIdTokenResult();
    const claims = result.claims as Partial<CustomClaims>;
    return claims;
};

const getAuthenticatedPages = () => {
    const path = router.state.location.pathname;
    return {
        profile: /^\/profile$/.test(path),
        report: /^\/organizations\/\w+\/submissions\/\w+\/reports\/\w+$/.test(path),
        settings: /^\/organizations\/\w+\/settings$/.test(path),
        verification: /^\/organizations\/\w+\/verification\/\w+\/reports\/\w+$/.test(path),
        category:
            /^\/organizations\/\w+\/submissions\/\w+\/reports\/\w+\/categories-overview$/.test(
                path,
            ),
    };
};

const stringToColor = (string: string) => {
    let hash = 0;
    let i;

    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }

    return color;
};

const sleep = async (ms: number = 1000) => {
    await new Promise(resolve => setTimeout(resolve, ms));
};

const formatPersonalNumber = (value: string) => {
    return value.substring(0, 8) + " " + "-" + " " + value.substring(8);
};

const formatFullAddress = (address: string | null, city: string | null) => {
    let str = "";

    if (address) str += address;

    if (city) {
        if (address) str += ", " + city;
        else str += city;
    }

    return str;
};

const formatNegativeAmount = (
    amount: number,
    currency: keyof typeof constant.CURRENCIES = "KRONA",
) => {
    if (Object.is(amount, -0)) return constant.CURRENCIES[currency].format(0);
    if (amount > 0) return "- " + constant.CURRENCIES[currency].format(amount);
    if (amount === 0) return constant.CURRENCIES[currency].format(amount);
    if (amount < 0) return constant.CURRENCIES[currency].format(amount);
    return constant.CURRENCIES[currency].format(amount);
};

type GenerateRandomFilenameOptions = {
    extension?: string;
    prefix?: string;
};

const generateRandomFilename = (options: GenerateRandomFilenameOptions = {}): string => {
    const { extension = "bin", prefix = "download" } = options;

    const now = new Date();
    const timestamp = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0"),
        String(now.getHours()).padStart(2, "0"),
        String(now.getMinutes()).padStart(2, "0"),
        String(now.getSeconds()).padStart(2, "0"),
    ].join("");

    const randomSlug = (() => {
        if (typeof crypto !== "undefined" && "getRandomValues" in crypto) {
            const bytes = new Uint8Array(8);
            crypto.getRandomValues(bytes);

            return Array.from(bytes)
                .map(b => b.toString(36).padStart(2, "0"))
                .join("")
                .slice(0, 16);
        }

        return Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);
    })();

    return `${prefix}-${timestamp}-${randomSlug}.${extension.replace(/^\./, "")}`;
};

const triggerFileDownload = (file: FileDescriptor) => {
    const { filename, url } = file;
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click(), link.remove();
};

// TINK is the pre-rename value for bank imports; already-submitted reports still carry it in Firestore.
const IMPORT_SOURCE_KEYS: Record<string, string> = {
    BANK: "importSourceBank",
    TINK: "importSourceBank",
    CSV: "importSourceFile",
    MANUAL: "importSourceManual",
};

const formatImportSource = (value?: string | null) =>
    value && IMPORT_SOURCE_KEYS[value] ? i18n.t(IMPORT_SOURCE_KEYS[value]) : "-";

export {
    setCurrentUser,
    getAuthenticatedPages,
    stringToColor,
    sleep,
    formatPersonalNumber,
    formatFullAddress,
    formatNegativeAmount,
    generateRandomFilename,
    triggerFileDownload,
    formatImportSource,
};
